import {GetInvalidPropPaths, GetSchemaJSON, observer_mgl} from "mobx-graphlink";
import {ErrorBoundary, GetUpdates, Observer, RunInAction} from "web-vcore";
import {Clone, DeepGet, DeepSet, FromJSON, StartDownload, ToJSON} from "js-vextensions";
import React from "react";
import {Button, Column, Row, Text, TextArea, TextInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {GetLiveSession_Reactive} from "../../../Engine/LiveSessionManagement.js";
import {GetEngineConfigPanelsToShow} from "../../../Engine/LiveSession/ComponentRegistry.js";
import {store} from "../../../Store/index.js";
import {GetSelectedEngineConfig, GetSelectedEngineConfigID} from "../../../Server/DB/engineConfigs.js";
import {EngineConfig} from "../../../Server/DB/engineConfigs/@EngineConfig.js";
import {EngineConfig_GetDefault, EngineConfig_GetDefault_Remote as EngineConfig_GetDefault_Client} from "../../../Server/DB/engineConfigs/@EngineConfig_Presets.js";
import {MeID} from "../../../Server/DB/users.js";
import {IsUserCreatorOrMod} from "../../../Server/DB/users/$user.js";
import {ShowSignInPopup} from "../../@Shared/NavBar/UserPanel.js";
import {SortKeysInObjectTree} from "../../../Utils/General/General.js";
import {DialogStyle} from "../../../Utils/UI/GlobalStyles.js";
import {EngineConfigPicker} from "../../Tools/@Shared/EngineConfigPicker.js";
import {SessionManagementPanel_Local} from "./LocalPanel/SessionManagementPanel.js";
import {ScrollView} from "react-vscrollview";
import {AddEngineConfig, DeleteEngineConfig, UpdateEngineConfig} from "../../../Server/Commands/EngineConfigCommands.js";
import {LiveSession_Local} from "../../../Engine/LiveSession_Local.js";
import moment from "moment";

export type EngineConfigChangerFunc = (config: EngineConfig)=>any;
export type EngineConfigPanels_SharedProps = {client: boolean, enabled: boolean, active: boolean, config_full: EngineConfig, ChangeConfig_Full: (configChangerFunc: EngineConfigChangerFunc)=>any}; // is also used for Remote panel

export function ExtendEngineConfigPanelSharedProps<T, T2>(props: EngineConfigPanels_SharedProps, compConfigGetter: (config: EngineConfig)=>T, compConfigSetter: (config: EngineConfig, value: T)=>any, otherProps?: T2) {
	const {config_full, ChangeConfig_Full} = props;
	return {
		...props,
		config: compConfigGetter(config_full),
		ChangeConfig: (compConfigChangerFunc: (config: T)=>any)=>{
			ChangeConfig_Full(c=>{
				const newValue = compConfigGetter(c);
				compConfigChangerFunc(newValue);
				compConfigSetter(c, newValue);
			});
		},
		...otherProps,
	};
}

export const ConfigPanel_Local = observer_mgl((props: {journeyUI: boolean})=>{
	const {journeyUI} = props;
	const config = GetSelectedEngineConfig()!;
	const liveSession = GetLiveSession_Reactive();
	const active = liveSession instanceof LiveSession_Local;
	const enabled = IsUserCreatorOrMod(MeID(), config) && (store.main.settings.configChangeDuringSession == "allow" ? true : !active);

	const ChangeConfig = (configChangerFunc: EngineConfigChangerFunc)=>{
		const newConfig = Clone(config);
		configChangerFunc(newConfig);
		new UpdateEngineConfig({id: config.id!, updates: GetUpdates(config, newConfig, true)}).RunOnServer();
	};

	const sharedProps = {client: false, enabled, active, config_full: config, ChangeConfig_Full: ChangeConfig};

	const Container = journeyUI ? ScrollView : Column;
	return (
		<Container style={{
			flex: 1,
		}}>
			{!journeyUI && <SessionManagementPanel_Local {...sharedProps}/>}
			<ConfigSelectRow {...sharedProps}/>
			{config &&
			<ErrorBoundary>
				{GetEngineConfigPanelsToShow(journeyUI).map((PanelClass, index)=>{
					return <PanelClass key={index} {...sharedProps}/>;
				})}
			</ErrorBoundary>}
			{!config &&
			<Text mt={10}>Select a configuration to see/edit its options.</Text>}
		</Container>
	);
});

export const ConfigSelectRow = observer_mgl((props: {style?: any} & EngineConfigPanels_SharedProps)=>{
	const {style, ...sharedProps} = props;
	const {enabled} = sharedProps;
	const configID = GetSelectedEngineConfigID();
	const config = GetSelectedEngineConfig()!; // kinda redundant (given props.config_full), but whatever
	const liveSession = GetLiveSession_Reactive();
	const active = liveSession instanceof LiveSession_Local;

	return (
		<Row style={style}>
			<Text>Config:</Text>
			<EngineConfigPicker containerStyle={{marginLeft: 5}} style={{flex: 1}} value={configID} onChange={val=>{
				RunInAction("LocalPanel.selectedConfig.onChange", ()=>store.main.engine.config.selectedConfig = val);
			}}/>
			<Button ml={5} enabled={!active} mdIcon="plus" title="New" style={{padding: "3px 7px"}} onClick={async()=>{
				if (MeID() == null) return ShowSignInPopup();
				const newConfig = new EngineConfig({name: "New config"});
				const {id: newConfigID} = await new AddEngineConfig({entry: newConfig}).RunOnServer();
				RunInAction("LocalPanel.newConfig.onClick", ()=>store.main.engine.config.selectedConfig = newConfigID);
			}}/>
			<Button ml={5} mdIcon="pencil" title="Rename" enabled={config != null && enabled} style={{padding: "3px 7px"}} onClick={()=>{
				let newName = config.name;

				//let error = null;
				const Change = (..._)=>boxController.UpdateUI();
				const boxController = ShowMessageBox({
					title: "Rename engine config", cancelButton: true,
					message: ()=>{
						//boxController.UpdateOptions({okButtonProps: {enabled: error == null}});
						return (
							<Column style={DialogStyle({width: 600})}>
								<Row>
									<Text>Name:</Text>
									<TextInput ml={5} style={{flex: 1}} value={newName} onChange={val=>Change(newName = val)}/>
								</Row>
							</Column>
						);
					},
					onOK: ()=>{
						new UpdateEngineConfig({id: config.id!, updates: {name: newName}}).RunOnServer();
					},
				});
			}}/>
			<Button ml={5} mdIcon="content-copy" title="Clone" enabled={config != null && !active} style={{padding: "3px 7px"}} onClick={async()=>{
				if (MeID() == null) return ShowSignInPopup();
				const newConfig = Clone(config) as EngineConfig;
				newConfig.baseConfig = config.id;
				newConfig.name += " (copy)";
				const {id: newConfigID} = await new AddEngineConfig({entry: newConfig}).RunOnServer();
				RunInAction("LocalPanel.Clone.onClick", ()=>store.main.engine.config.selectedConfig = newConfigID);
			}}/>
			<Button ml={5} mdIcon="download" title="Export" enabled={config != null} style={{padding: "3px 7px"}} onClick={()=>{
				const configCopy = SortKeysInObjectTree(config);
				const json = JSON.stringify(configCopy, undefined, "\t");
				StartDownload(json, `LFConfig_${config.name}_${moment().format("YYYY-MM-DD-HH-mm-ss")}.json`);
			}}/>
			<JSONEngineConfigButton {...sharedProps}/>
			<FixEngineConfigButton {...sharedProps}/>
			<ResetEngineConfigButton {...sharedProps}/>
			<Button ml={5} mdIcon="delete" title="Delete" enabled={config != null && enabled} style={{padding: "3px 7px"}} onClick={()=>{
				ShowMessageBox({
					title: "Delete engine config?", cancelButton: true,
					message: "Permanently delete this engine configuration?",
					onOK: ()=>{
						new DeleteEngineConfig({id: config.id!}).RunOnServer();
					},
				});
			}}/>
		</Row>
	);
});

export const JSONEngineConfigButton = observer_mgl((props: {} & EngineConfigPanels_SharedProps)=>{
	const {enabled, active, config_full, ChangeConfig_Full} = props;
	return (
		<Button ml={5} text="JSON" enabled={config_full != null && enabled} style={{fontSize: 12, padding: "3px 7px"}} onClick={()=>{
			//let newJSON = ToJSON_WithSpaces(config);
			const configCopy = SortKeysInObjectTree(config_full);
			let newJSON = ToJSON(configCopy, undefined, 4);

			const boxController = ShowMessageBox({
				title: `${enabled ? "Edit" : "View"} engine config json`, cancelButton: true,
				message: ()=>(
					//<Column style={DialogStyle({width: 800})}>
					<Column>
						<Row>Config:</Row>
						<TextArea enabled={enabled} style={{width: 800..KeepAtMost(window.innerWidth - 100), height: window.innerHeight - 200}} value={newJSON} onChange={val=>{
							newJSON = val;
							boxController.UpdateUI();
						}}/>
					</Column>
				),
				okButtonProps: {enabled},
				okOnEnterKey: false, // enter key needed for adding new-lines!
				onOK: ()=>{
					g.oldEngineConfigs = (g.oldEngineConfigs || []).concat(config_full);
					const newJSONObj = FromJSON(newJSON);
					/*ChangeConfig(c=>{
						c.VKeys().forEach(key=>Reflect.deleteProperty(c, key));
						//ProcessDBData(newJSONObj, false, true, null); // add back the helpers (eg. entry.id) // actually, not needed since only root config.id is used
						newJSONObj.VKeys().forEach(key=>{
							c[key] = newJSONObj[key];
						});
					});*/
					new UpdateEngineConfig({id: config_full.id!, newData: newJSONObj}).RunOnServer();
				},
			});
		}}/>
	);
});
export const FixEngineConfigButton = observer_mgl((props: {} & EngineConfigPanels_SharedProps)=>{
	const {enabled, config_full, ChangeConfig_Full} = props;
	return (
		<Button ml={5} text="Fix" enabled={config_full != null && enabled} style={{fontSize: 12, padding: "3px 7px"}} onClick={()=>{
			//const keysToRemove = config.VKeys(true).filter(key=>GetSchemaJSON("EngineConfig").properties[key] == null);
			let keysToRemove = GetInvalidPropPaths(Clone(config_full), GetSchemaJSON("EngineConfig")).map(a=>a.propPath);

			// fix that for "root keys", the function above includes an extra "/" at the start (confusing our DeepSet calls below)
			keysToRemove = keysToRemove.map(key=>key.startsWith("/") ? key.slice(1) : key);
			// todo: move this fix into the GetInvalidPropPaths func

			ShowMessageBox({
				title: "Fix config?", cancelButton: true,
				message: ()=><Text sel style={DialogStyle({whiteSpace: "pre-wrap"})}>{`
					Remove any invalid properties in the configuration?

					Keys to remove:${keysToRemove.length == 0 ? " (none)" : keysToRemove.map(key=>`\n* ${key}: ${DeepGet(config_full, key)}`).join("")}
				`.AsMultiline(0)}</Text>,
				onOK: ()=>{
					g.oldEngineConfigs = (g.oldEngineConfigs || []).concat(config_full);
					const oldConfig = config_full;

					// we can't use ChangeConfig_Full here, because we want to set skipValidateKeysBeingUpdated to true on the command, before calling Run()
					const newConfig = Clone(oldConfig) as typeof oldConfig;
					for (const key of keysToRemove) {
						//delete c[key];
						DeepSet(newConfig, key, undefined, undefined, undefined, true);
					}
					//const updates = GetUpdates(oldConfig, newConfig, true, false);
					new UpdateEngineConfig({id: oldConfig.id!, newData: newConfig}).RunOnServer();
				},
			});
		}}/>
	);
});
export const ResetEngineConfigButton = observer_mgl((props: {} & EngineConfigPanels_SharedProps)=>{
	const {enabled, config_full, ChangeConfig_Full, client} = props;
	return (
		<Button ml={5} text="Reset" enabled={config_full != null && enabled} style={{fontSize: 12, padding: "3px 7px"}} onClick={()=>{
			//const keysToReset = GetInvalidPropPaths(WithoutHelpers(config), GetSchemaJSON("EngineConfig")).map(a=>a.propPath);
			const defaultConfig = client ? EngineConfig_GetDefault_Client() : EngineConfig_GetDefault();
			//const keysToReset = defaultConfig.VKeys().map(key=>`\n* ${key}: ${defaultConfig[key]}`).join("");

			let pathsToReset = [] as string[];
			const controller = ShowMessageBox({
				title: "Reset config?", cancelButton: true, okOnEnterKey: false,
				message: ()=><Column style={DialogStyle({})}>
					<Text>Enter the data-paths within the config, to reset to default values: (one line per path) [to reset all: ALL]</Text>
					<TextArea autoSize={true} value={pathsToReset.join("\n")} onChange={val=>{
						pathsToReset = val.split("\n");
						controller.UpdateUI();
					}}/>
				</Column>,
				onOK: ()=>{
					g.oldEngineConfigs = (g.oldEngineConfigs || []).concat(config_full);
					ChangeConfig_Full(c=>{
						if (pathsToReset.length == 1 && pathsToReset[0] == "ALL") {
							c.VSet(defaultConfig);
						} else {
							for (const path of pathsToReset) {
								const defaultVal = DeepGet(defaultConfig, path);
								DeepSet(c, path, defaultVal);
							}
						}
					});
				},
			});
		}}/>
	);
});